// @ts-nocheck
/**
 * result.ts — WP-009 Diagnostics Model
 *
 * Pure utility functions for constructing, combining, and querying
 * ValidationResult objects. All functions are pure with no side effects.
 *
 * No imports from outside src/v2/ — zero framework dependencies.
 */

import type {
    DiagnosticReport,
    DiagnosticSeverity,
    ValidationCode,
    ValidationIssue,
    ValidationResult,
} from './types';

// ── Factories ──────────────────────────────────────────────────────────────

/**
 * Creates a passing ValidationResult with no issues.
 */
export function makePassResult(): ValidationResult {
  return { valid: true, issues: [] };
}

/**
 * Creates a failing ValidationResult with a single error.
 */
export function makeFailResult(
  code: ValidationCode,
  message: string,
  opts?: { path?: string; relatedPaths?: string[]; suggestion?: string },
): ValidationResult {
  const issue: ValidationIssue = {
    severity: 'error',
    code,
    message,
    ...(opts?.path !== undefined ? { path: opts.path } : {}),
    ...(opts?.relatedPaths !== undefined ? { relatedPaths: opts.relatedPaths } : {}),
    ...(opts?.suggestion !== undefined ? { suggestion: opts.suggestion } : {}),
  };
  return { valid: false, issues: [issue] };
}

/**
 * Creates a ValidationResult with a single warning (still valid).
 */
export function makeWarnResult(
  code: ValidationCode,
  message: string,
  opts?: { path?: string; suggestion?: string },
): ValidationResult {
  const issue: ValidationIssue = {
    severity: 'warning',
    code,
    message,
    ...(opts?.path !== undefined ? { path: opts.path } : {}),
    ...(opts?.suggestion !== undefined ? { suggestion: opts.suggestion } : {}),
  };
  return { valid: true, issues: [issue] };
}

// ── Combinators ────────────────────────────────────────────────────────────

/**
 * Merges two or more ValidationResult objects into one.
 * valid = all inputs are valid.
 * issues = all issues from all inputs, in order.
 */
export function mergeResults(...results: ValidationResult[]): ValidationResult {
  const allIssues = results.flatMap(r => [...r.issues]);
  const valid = !allIssues.some(i => i.severity === 'error');
  return { valid, issues: allIssues };
}

/**
 * Adds a single issue to an existing ValidationResult.
 * Returns a new ValidationResult (immutable).
 * Recalculates valid based on whether any error exists.
 */
export function addIssue(result: ValidationResult, issue: ValidationIssue): ValidationResult {
  const issues = [...result.issues, issue];
  const valid = !issues.some(i => i.severity === 'error');
  return { valid, issues };
}

// ── Query helpers ──────────────────────────────────────────────────────────

/**
 * Filters issues by severity.
 */
export function filterBySeverity(
  result: ValidationResult,
  severity: DiagnosticSeverity,
): ValidationIssue[] {
  return result.issues.filter(i => i.severity === severity);
}

/**
 * Returns true if the result has any issues with the given code.
 */
export function hasCode(result: ValidationResult, code: ValidationCode): boolean {
  return result.issues.some(i => i.code === code);
}

/**
 * Counts issues by severity.
 */
export function countBySeverity(result: ValidationResult): {
  errors: number;
  warnings: number;
  infos: number;
} {
  return {
    errors: result.issues.filter(i => i.severity === 'error').length,
    warnings: result.issues.filter(i => i.severity === 'warning').length,
    infos: result.issues.filter(i => i.severity === 'info').length,
  };
}

// ── Report builder ─────────────────────────────────────────────────────────

/**
 * Builds a DiagnosticReport from a ValidationResult.
 */
export function buildReport(result: ValidationResult, templateId?: string): DiagnosticReport {
  const report: DiagnosticReport = {
    valid: result.valid,
    issues: [...result.issues],
    summary: countBySeverity(result),
    generatedAt: new Date().toISOString(),
  };
  if (templateId !== undefined) {
    report.templateId = templateId;
  }
  return report;
}
